import { queryOptions } from "@tanstack/react-query";
import {
  getScriptReview,
  getScriptReviewStatus,
  listScriptReviews,
  isTerminalScriptReviewStatus,
  type ScriptReviewStatus,
  type ScriptReviewStatusResult,
} from "@/features/script-review/api/script-review.api";
import { fetchScripts } from "@/features/opportunities/api/opportunities.api";
import {
  PREFLIGHT_POLL_FAST_MS,
  PREFLIGHT_POLL_FAST_WINDOW_MS,
  PREFLIGHT_POLL_MED_MS,
  PREFLIGHT_POLL_MED_WINDOW_MS,
  PREFLIGHT_POLL_SLOW_MS,
} from "@/lib/query-constants";

function normTid(tenantId: string | null | undefined): string {
  return tenantId || "__none__";
}

export const scriptReviewKeys = {
  all: ["script-review"] as const,
  list: (tenantId: string) => ["script-review", "list", tenantId] as const,
  detail: (tenantId: string, id: string) => ["script-review", "detail", tenantId, id] as const,
  status: (tenantId: string, id: string, pollStartedAt = 0) =>
    ["script-review", "status", tenantId, id, pollStartedAt] as const,
  scriptsForSet: (setId: string) => ["script-review", "scripts-for-set", setId] as const,
} as const;

/** This org's reviews (newest first) for the history table. */
export function scriptReviewListQueryOptions(tenantId: string | null | undefined) {
  return queryOptions({
    queryKey: scriptReviewKeys.list(normTid(tenantId)),
    queryFn: () => listScriptReviews({ limit: 50 }),
    enabled: !!tenantId,
  });
}

export function scriptReviewDetailQueryOptions(tenantId: string | null | undefined, id: string) {
  return queryOptions({
    queryKey: scriptReviewKeys.detail(normTid(tenantId), id),
    queryFn: () => getScriptReview(id),
    enabled: !!tenantId && !!id,
  });
}

/** Relaxing-backoff poll interval (mirrors the preflight ramp). */
export function scriptReviewPollInterval(
  status: ScriptReviewStatus | undefined,
  pollStartedAt: number,
  now: number = Date.now(),
): number | false {
  if (!pollStartedAt) return false;
  if (isTerminalScriptReviewStatus(status)) return false;
  const elapsed = now - pollStartedAt;
  if (elapsed < PREFLIGHT_POLL_FAST_WINDOW_MS) return PREFLIGHT_POLL_FAST_MS;
  if (elapsed < PREFLIGHT_POLL_MED_WINDOW_MS) return PREFLIGHT_POLL_MED_MS;
  return PREFLIGHT_POLL_SLOW_MS;
}

/** Per-review status poll; stops on terminal status. */
export function scriptReviewStatusQueryOptions(
  tenantId: string | null | undefined,
  id: string,
  pollStartedAt: number,
) {
  return queryOptions<ScriptReviewStatusResult>({
    queryKey: scriptReviewKeys.status(normTid(tenantId), id, pollStartedAt),
    queryFn: () => getScriptReviewStatus(id),
    enabled: !!tenantId && !!id && pollStartedAt > 0,
    refetchInterval: (query) => scriptReviewPollInterval(query.state.data?.status, pollStartedAt),
    meta: { skipGlobalError: true },
  });
}

/** Scripts within a set, for the second dropdown. */
export function scriptsForSetQueryOptions(setId: string | null | undefined) {
  return queryOptions({
    queryKey: scriptReviewKeys.scriptsForSet(setId ?? "__none__"),
    queryFn: () => fetchScripts({ setId: setId as string, limit: 100 }),
    enabled: !!setId,
  });
}
